import { useState, useContext } from 'react';
import Modal from 'react-bootstrap/Modal';
import { i18n, i18nTemplate } from 'app/utils/i18n';
import { UserContext } from 'app/state/contexts';
import { useGetDocument } from 'app/api/documents/document-queries';
import { useStyle, useStyles } from 'app/api/style-queries';
import { useParams } from 'react-router';
import Dropdown from 'app/components/Dropdown';
import { usePermissions } from 'app/utils/hooks/document-permission';
import { ClassDefinition, ToolbarType, ScopeType } from './ClassEditor';
import { ClassMap } from '../utils/class-map';
import { labelIsTaken } from '../utils/class-lists';
import { ThemeColorPicker } from './ThemeColorPicker';
import { lintCustomCss } from '../utils/scss-formatting';
import { isReservedSelector, isBuiltinSelector } from '../utils/builtin-selectors';
import { CLEAR_FONT, defaultFontOptions, FontItem, extractFontName } from '../utils/font-options';
import './ClassStyleModal.scss';

const elementOptions = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'td', 'th', 'span', 'div'];

const fontWeightOptions = [
  { value: '', label: '' },
  { value: 'normal', label: 'dc.modal.weight-normal' },
  { value: 'bold', label: 'dc.modal.weight-bold' },
  { value: '100', label: '100' },
  { value: '200', label: '200' },
  { value: '300', label: '300' },
  { value: '400', label: '400' },
  { value: '500', label: '500' },
  { value: '600', label: '600' },
  { value: '700', label: '700' },
  { value: '800', label: '800' },
  { value: '900', label: '900' },
];

interface ClassStyleModalProps {
  show: boolean;
  onHide: () => void;
  onSave: (className: string, def: ClassDefinition, oldClassName?: string) => void;
  existingClassNames: string[];
  editClassName?: string | null;
  editDef?: ClassDefinition | null;
  prefillName?: string;
  classMap?: ClassMap;
}

export function ClassStyleModal({ show, onHide, onSave, existingClassNames, editClassName, editDef, prefillName, classMap }: ClassStyleModalProps) {
  const isEditing = !!editClassName;

  const [className, setClassName] = useState(editClassName ?? prefillName ?? '');
  const [element, setElement] = useState(editDef?.elements[0] ?? '');
  const [labelSv, setLabelSv] = useState(editDef?.labelSv ?? '');
  const [labelEn, setLabelEn] = useState(editDef?.labelEn ?? '');
  const [fontFamily, setFontFamily] = useState(editDef?.properties['font-family'] ?? '');
  const [fontSize, setFontSize] = useState(editDef?.properties['font-size'] ?? '');
  const [lineHeight, setLineHeight] = useState(editDef?.properties['line-height'] ?? '');
  const [letterSpacing, setLetterSpacing] = useState(editDef?.properties['letter-spacing'] ?? '');
  const [fontWeight, setFontWeight] = useState(editDef?.properties['font-weight'] ?? '');
  const [color, setColor] = useState(editDef?.properties['color'] ?? '');
  const [marginTop, setMarginTop] = useState(editDef?.properties['margin-top'] ?? '');
  const [marginRight, setMarginRight] = useState(editDef?.properties['margin-right'] ?? '');
  const [marginBottom, setMarginBottom] = useState(editDef?.properties['margin-bottom'] ?? '');
  const [marginLeft, setMarginLeft] = useState(editDef?.properties['margin-left'] ?? '');
  const [boldFontFamily, setBoldFontFamily] = useState(editDef?.boldFontFamily ?? '');
  const [italicFontFamily, setItalicFontFamily] = useState(editDef?.italicFontFamily ?? '');
  const [inToolbar, setInToolbar] = useState(editDef?.toolbars?.includes('toolbar') ?? true);
  const [inFinancialToolbar, setInFinancialToolbar] = useState(editDef?.toolbars?.includes('financial-toolbar') ?? false);
  const [comment, setComment] = useState(editDef?.comment ?? '');
  const [scopePage, setScopePage] = useState(editDef?.scopes?.includes('page') ?? false);
  const [scopeTable, setScopeTable] = useState(editDef?.scopes?.includes('table') ?? false);
  const isAllCommentedOut = editDef?.customCss?.trim()
    ? editDef.customCss
        .trim()
        .split('\n')
        .every((line) => line === '' || line.startsWith('// '))
    : false;
  const [showCustomCss, setShowCustomCss] = useState(!!editDef?.customCss && !isAllCommentedOut);
  const [customCss, setCustomCss] = useState(
    isAllCommentedOut
      ? editDef!
          .customCss!.trim()
          .split('\n')
          .map((line) => line.replace(/^\/\/ /, ''))
          .join('\n')
      : editDef?.customCss ?? ''
  );
  const [customCssError, setCustomCssError] = useState<string | null>(null);

  const { documentId } = useParams<{ documentId: string }>();
  const { selectedOrganization } = useContext(UserContext);
  const { documentPermissions } = usePermissions(documentId);
  const isCoder = documentPermissions.includes('CODER');
  const pDocument = useGetDocument(documentId, selectedOrganization);
  const fontIds = pDocument.data?.content.design.filter((s: any) => s.type === 'font').map((font: any) => font.styleId) ?? [];
  const styleQuery = useStyles({ styleIds: fontIds, organization: selectedOrganization, documentRevisionId: pDocument.data?.revisionId });

  const gvStyleId = pDocument.data?.content.design.find((d: any) => d.type === 'graphical-variables')?.styleId;
  const gvStyleQuery = useStyle({ organization: selectedOrganization, documentRevisionId: pDocument.data?.revisionId, styleId: gvStyleId });
  const themeColors: Record<string, string> = gvStyleQuery.data?.content ?? {};

  const customFonts =
    styleQuery.data
      ?.map((s: any) => extractFontName(s.content))
      .filter(Boolean)
      .map((name: string) => ({ value: name, title: name })) ?? [];
  const clearFontOption = { value: CLEAR_FONT, title: i18n('dc.modal.clear-font') };
  const allFontOptions = [clearFontOption, ...defaultFontOptions, ...customFonts];

  const handleFontChange = (setter: (v: string) => void) => (v: string) => setter(v === CLEAR_FONT ? '' : v);

  const sanitizedName = className
    .trim()
    .replace(/^\./, '')
    .replace(/[^a-zA-Z0-9_-]/g, '');
  const isValidCssName = /^[a-zA-Z_][a-zA-Z0-9_-]*$/.test(sanitizedName);
  const isRenamed = isEditing && sanitizedName !== editClassName;
  const isDuplicate = (!isEditing || isRenamed) && existingClassNames.includes(sanitizedName);
  const isReserved = (!isEditing || isRenamed) && isReservedSelector(sanitizedName);
  const isNameValid = sanitizedName.length > 0 && isValidCssName && !isDuplicate && !isReserved;
  const compoundSelector = element && sanitizedName ? `${element}.${sanitizedName}` : sanitizedName ? `.${sanitizedName}` : '';
  const labelClash = classMap ? labelIsTaken(classMap, labelSv, editClassName ?? sanitizedName) : null;

  const handleSave = () => {
    if (!isNameValid) return;

    let lintedCustomCss = '';
    if (customCss.trim()) {
      const lint = lintCustomCss(customCss);
      if (!lint.balanced) {
        setCustomCssError(i18n('dc.modal.css-unbalanced'));
        return;
      }
      lintedCustomCss = lint.output;
      setCustomCss(lint.output);
    }
    setCustomCssError(null);

    const properties: Record<string, string> = {};
    if (fontFamily) properties['font-family'] = fontFamily;
    if (fontSize) properties['font-size'] = fontSize;
    if (lineHeight) properties['line-height'] = lineHeight;
    if (letterSpacing) properties['letter-spacing'] = letterSpacing;
    if (fontWeight) properties['font-weight'] = fontWeight;
    if (color) properties['color'] = color;
    if (marginTop) properties['margin-top'] = marginTop;
    if (marginRight) properties['margin-right'] = marginRight;
    if (marginBottom) properties['margin-bottom'] = marginBottom;
    if (marginLeft) properties['margin-left'] = marginLeft;

    const toolbars: ToolbarType[] = [];
    if (inToolbar) toolbars.push('toolbar');
    if (inFinancialToolbar) toolbars.push('financial-toolbar');

    const scopes: ScopeType[] = [];
    if (scopePage) scopes.push('page');
    if (scopeTable) scopes.push('table');

    const def: ClassDefinition = {
      elements: element ? [element] : [],
      properties,
      labelSv: labelSv || sanitizedName,
      labelEn: labelEn || sanitizedName,
      comment: comment.trim() || undefined,
      customCss: lintedCustomCss
        ? showCustomCss
          ? lintedCustomCss
          : lintedCustomCss
              .split('\n')
              .map((line) => (line ? `// ${line}` : ''))
              .join('\n')
        : undefined,
      boldFontFamily: boldFontFamily || undefined,
      italicFontFamily: italicFontFamily || undefined,
      toolbars,
      scopes: scopes.length > 0 ? scopes : undefined,
    };

    onSave(sanitizedName, def, isRenamed ? editClassName! : undefined);
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable dialogClassName="class-style-modal-dialog">
      <Modal.Header closeButton>
        <Modal.Title>{isEditing ? i18n('dc.modal.edit-title') : i18n('dc.modal.new-title')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="class-style-modal">
          <div className="row mb-3">
            <div className="col-auto">
              <label className="form-label fs-7">{i18n('dc.modal.element')}</label>
              <select className="form-select form-select-sm" value={element} onChange={(e) => setElement(e.target.value)} style={{ width: 100 }}>
                <option value="">—</option>
                {elementOptions.map((el) => (
                  <option key={el} value={el}>
                    {el}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-auto">
              <label className="form-label fs-7">{i18n('dc.modal.classname')}</label>
              <input
                type="text"
                className={`form-control form-control-sm ${className.trim() && !isNameValid ? 'is-invalid' : ''}`}
                value={className}
                onChange={(e) => setClassName(e.target.value)}
                placeholder={i18n('dc.modal.classname-placeholder')}
                disabled={false}
                style={{ width: 180 }}
              />
              {className.trim() && !isValidCssName && <div className="invalid-feedback">{i18n('dc.modal.classname-invalid')}</div>}
              {isDuplicate && <div className="invalid-feedback">{i18n('dc.modal.classname-duplicate')}</div>}
              {isReserved && (
                <div className="invalid-feedback">
                  {isBuiltinSelector(sanitizedName) ? i18n('dc.modal.classname-reserved-slot') : i18n('dc.modal.classname-reserved')}
                </div>
              )}
            </div>
            <div className="col ms-auto text-start">
              <label className="form-label fs-7">{i18n('dc.modal.toolbar')}</label>
              <div className="d-flex justify-content-start gap-3 align-items-center" style={{ height: 31 }}>
                <div className="form-check">
                  <input className="form-check-input" type="checkbox" id="toolbarEditor" checked={inToolbar} onChange={() => setInToolbar(!inToolbar)} />
                  <label className="form-check-label fs-7" htmlFor="toolbarEditor">
                    {i18n('dc.modal.toolbar-editor')}
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="toolbarFinancial"
                    checked={inFinancialToolbar}
                    onChange={() => setInFinancialToolbar(!inFinancialToolbar)}
                  />
                  <label className="form-check-label fs-7" htmlFor="toolbarFinancial">
                    {i18n('dc.modal.toolbar-financial')}
                  </label>
                </div>
              </div>
              <label className="form-label fs-7 mt-2 mb-0">{i18n('dc.modal.scope')}</label>
              <div className="d-flex justify-content-start gap-3 align-items-center" style={{ height: 31 }}>
                <div className="form-check">
                  <input className="form-check-input" type="checkbox" id="scopePage" checked={scopePage} onChange={() => setScopePage(!scopePage)} />
                  <label className="form-check-label fs-7" htmlFor="scopePage">
                    {i18n('dc.modal.scope-page')}
                  </label>
                </div>
                <div className="form-check">
                  <input className="form-check-input" type="checkbox" id="scopeTable" checked={scopeTable} onChange={() => setScopeTable(!scopeTable)} />
                  <label className="form-check-label fs-7" htmlFor="scopeTable">
                    {i18n('dc.modal.scope-table')}
                  </label>
                </div>
              </div>
            </div>
          </div>
          {compoundSelector && (
            <div className="mb-3">
              <code className="class-style-modal__selector">{compoundSelector}</code>
            </div>
          )}
          <div className="row mb-3">
            <div className="col-auto">
              <label className="form-label fs-7">{i18n('dc.modal.name-sv')}</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={labelSv}
                onChange={(e) => setLabelSv(e.target.value)}
                placeholder={sanitizedName || i18n('dc.modal.name-placeholder')}
                style={{ width: 180 }}
              />
              {labelClash && <div className="form-text text-warning fs-7">{i18nTemplate('dc.modal.label-taken', { className: `.${labelClash}` })}</div>}
            </div>
            <div className="col-auto">
              <label className="form-label fs-7">{i18n('dc.modal.name-en')}</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={labelEn}
                onChange={(e) => setLabelEn(e.target.value)}
                placeholder={sanitizedName || i18n('dc.modal.name-placeholder')}
                style={{ width: 180 }}
              />
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label fs-7">{i18n('dc.modal.comment')}</label>
            <textarea
              className="form-control form-control-sm"
              rows={2}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={i18n('dc.modal.comment-placeholder')}
              style={{ resize: 'vertical' }}
            />
          </div>

          <hr />

          <label className="form-label fs-7 mb-1">{i18n('dc.modal.font')}</label>
          <div className="row mb-2">
            <div className="col-auto">
              <label className="form-label fs-7 text-muted">{i18n('dc.modal.font-normal')}</label>
              <div style={{ width: 180 }}>
                <Dropdown
                  items={allFontOptions}
                  value={fontFamily}
                  onChange={handleFontChange(setFontFamily)}
                  wrapper={FontItem}
                  sm
                  placeHolder={i18n('dc.modal.select-font')}
                  disabled={false}
                  groupbyKey={undefined}
                  children={undefined}
                  filter={false}
                />
              </div>
            </div>
            <div className="col-auto">
              <label className="form-label fs-7 text-muted">{i18n('dc.modal.font-bold')}</label>
              <div style={{ width: 180 }}>
                <Dropdown
                  items={allFontOptions}
                  value={boldFontFamily}
                  onChange={handleFontChange(setBoldFontFamily)}
                  wrapper={FontItem}
                  sm
                  placeHolder={i18n('dc.modal.same-as-document')}
                  disabled={false}
                  groupbyKey={undefined}
                  children={undefined}
                  filter={false}
                />
              </div>
            </div>
            <div className="col-auto">
              <label className="form-label fs-7 text-muted">{i18n('dc.modal.font-italic')}</label>
              <div style={{ width: 180 }}>
                <Dropdown
                  items={allFontOptions}
                  value={italicFontFamily}
                  onChange={handleFontChange(setItalicFontFamily)}
                  wrapper={FontItem}
                  sm
                  placeHolder={i18n('dc.modal.same-as-document')}
                  disabled={false}
                  groupbyKey={undefined}
                  children={undefined}
                  filter={false}
                />
              </div>
            </div>
          </div>

          <div className="row mb-2">
            <div className="col-auto">
              <label className="form-label fs-7">{i18n('dc.modal.weight')}</label>
              <select className="form-select form-select-sm" value={fontWeight} onChange={(e) => setFontWeight(e.target.value)} style={{ width: 120 }}>
                {fontWeightOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label.startsWith('dc.') ? i18n(opt.label) : opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-auto">
              <label className="form-label fs-7">{i18n('dc.modal.size')}</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={fontSize}
                onChange={(e) => setFontSize(e.target.value)}
                placeholder="18px"
                style={{ width: 120 }}
              />
            </div>
            <div className="col-auto">
              <label className="form-label fs-7">{i18n('dc.modal.line-height')}</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={lineHeight}
                onChange={(e) => setLineHeight(e.target.value)}
                placeholder="150%"
                style={{ width: 120 }}
              />
            </div>
            <div className="col-auto">
              <label className="form-label fs-7">{i18n('dc.modal.letter-spacing')}</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={letterSpacing}
                onChange={(e) => setLetterSpacing(e.target.value)}
                placeholder="0.02em"
                style={{ width: 120 }}
              />
            </div>
          </div>
          <hr />

          <label className="form-label fs-7 mb-1">{i18n('dc.modal.color')}</label>
          <ThemeColorPicker value={color} onChange={setColor} themeColors={themeColors} />

          <hr />

          <label className="form-label fs-7 mb-1">{i18n('dc.modal.margins')}</label>
          <div className="row mb-2">
            <div className="col-auto">
              <label className="form-label fs-7 text-muted">{i18n('dc.modal.margin-top')}</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={marginTop}
                onChange={(e) => setMarginTop(e.target.value)}
                placeholder="16px"
                style={{ width: 100 }}
              />
            </div>
            <div className="col-auto">
              <label className="form-label fs-7 text-muted">{i18n('dc.modal.margin-right')}</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={marginRight}
                onChange={(e) => setMarginRight(e.target.value)}
                placeholder="0"
                style={{ width: 100 }}
              />
            </div>
            <div className="col-auto">
              <label className="form-label fs-7 text-muted">{i18n('dc.modal.margin-bottom')}</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={marginBottom}
                onChange={(e) => setMarginBottom(e.target.value)}
                placeholder="8px"
                style={{ width: 100 }}
              />
            </div>
            <div className="col-auto">
              <label className="form-label fs-7 text-muted">{i18n('dc.modal.margin-left')}</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={marginLeft}
                onChange={(e) => setMarginLeft(e.target.value)}
                placeholder="0"
                style={{ width: 100 }}
              />
            </div>
          </div>

          <hr />

          {isCoder && (
            <div className="d-flex align-items-center gap-2 mb-2">
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="customCssToggle"
                  checked={showCustomCss}
                  onChange={() => setShowCustomCss(!showCustomCss)}
                />
                <label className="form-check-label fs-7" htmlFor="customCssToggle">
                  {i18n('dc.modal.custom-css')}
                </label>
              </div>
            </div>
          )}

          {isCoder && showCustomCss && (
            <>
              <div className="alert alert-info py-2 fs-7">
                <i className="fa-regular fa-circle-info me-1" />
                {i18n('dc.modal.css-info')}
              </div>
              {customCssError && (
                <div className="alert alert-danger py-2 fs-7">
                  <i className="fa-regular fa-circle-exclamation me-1" />
                  {customCssError}
                </div>
              )}
              <textarea
                className={`form-control form-control-sm ${customCssError ? 'is-invalid' : ''}`}
                rows={5}
                value={customCss}
                onChange={(e) => {
                  setCustomCss(e.target.value);
                  if (customCssError) setCustomCssError(null);
                }}
                placeholder={'border-bottom: 1px solid #333;\n&:first-child { margin-top: 0; }\n.xrp-body-bold { font-weight: 700; }'}
                style={{
                  fontFamily: 'ui-monospace, monospace',
                  fontSize: '0.8125rem',
                  lineHeight: 1.5,
                  tabSize: 2,
                  resize: 'none',
                  minHeight: '8em',
                  fieldSizing: 'content',
                }}
              />
            </>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-sm btn-secondary" onClick={onHide}>
          {i18n('dc.modal.cancel')}
        </button>
        <button className="btn btn-sm btn-primary" onClick={handleSave} disabled={!isNameValid}>
          {isEditing ? i18n('dc.modal.save') : i18n('dc.modal.add')}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
